import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard, BookOpen, BarChart3, History,
  UserCircle2, Settings, LogOut, Menu, X, Code2,
  ClipboardList, ClipboardCheck, GraduationCap, Award,
} from "lucide-react";
import { cn } from "./cn";
import { isCodingProfilesEnabled } from "../services/codingProfile.service";
import { BrandIcon, useBrand } from "./BrandMark";

/**
 * Mobile navigation — bottom tab bar (primary, thumb-reachable) + a slide-in
 * drawer (full nav). Both are md:hidden; the desktop rail (Sidebar) is
 * hidden below md. Keeps its own small nav config so it stays independent
 * of the desktop rail's styling data.
 */

// ── Exam-only build: exam-forward student mobile nav. ──
const STUDENT_MAIN = [
  { id: "dashboard", tKey: "sidebar.dashboard", shortKey: "sidebar.short_home", label: "Home",    icon: LayoutDashboard, to: "/student/dashboard" },
  { id: "exams",     tKey: "sidebar.examinations", shortKey: "sidebar.short_exams", label: "Exams", icon: ClipboardList,  to: "/student/exams" },
  { id: "results",   tKey: "sidebar.my_results", shortKey: "sidebar.short_results", label: "Results",  icon: GraduationCap, to: "/my-results", matches: ["/student/course/"] },
  { id: "analytics", tKey: "sidebar.my_analytics", shortKey: "sidebar.short_analytics", label: "Analytics", icon: BarChart3,    to: "/student/analytics" },
];
const STUDENT_MORE = [
  // Registering for a test, and the admit card (docs/30 F12 — the page had no link anywhere).
  { id: "registration",   tKey: "sidebar.exam_registration", label: "Exam registration", icon: ClipboardCheck, to: "/exam-registration" },
  { id: "certificates",   tKey: "sidebar.my_certificates", label: "My Certificates", icon: Award,   to: "/my-certificates" },
  { id: "coding-profile", tKey: "sidebar.coding_profile", label: "Coding Profile",  icon: Code2,   to: "/student/coding-profile" },
  { id: "profile",  tKey: "sidebar.profile",          label: "Profile",          icon: UserCircle2, to: "/profile" },
  { id: "account",  tKey: "sidebar.account_settings", label: "Account settings", icon: Settings,    to: "/student/profile/account" },
];

const TEACHER_MAIN = [
  { id: "dashboard", tKey: "sidebar.dashboard",  shortKey: "sidebar.short_home",    label: "Home",      icon: LayoutDashboard, to: "/teacher/dashboard" },
  { id: "courses",   tKey: "sidebar.my_courses", shortKey: "sidebar.short_courses", label: "Courses",   icon: BookOpen,        to: "/teacher/courses", matches: ["/teacher/course/"] },
  // Exams, not the LMS's Timetable (its API is outside the exam build — a 404).
  { id: "exams",     tKey: "sidebar.examinations", shortKey: "sidebar.short_exams", label: "Exams",   icon: ClipboardList,   to: "/teacher/exams-home", matches: ["/teacher/exams/", "/teacher/exam/", "/teacher/question-bank/", "/teacher/groups/"] },
  { id: "activity",  tKey: "sidebar.activity_log", shortKey: "sidebar.short_activity", label: "Activity",  icon: History,         to: "/teacher/activity-log" },
];
const TEACHER_MORE = [
  { id: "profile", tKey: "sidebar.profile",          label: "Profile",          icon: UserCircle2, to: "/profile" },
  { id: "account", tKey: "sidebar.account_settings", label: "Account settings", icon: Settings,    to: "/teacher/profile/account" },
];

function navFor(role) {
  return role === "teacher"
    ? { main: TEACHER_MAIN, more: TEACHER_MORE }
    : { main: STUDENT_MAIN, more: STUDENT_MORE };
}

function isActivePath(itemTo, currentPath, matches) {
  if (!itemTo) return false;
  const base = itemTo.split("?")[0];
  if (itemTo === currentPath) return true;
  if (currentPath.startsWith(base + "/")) return true;
  if (Array.isArray(matches)) {
    for (const m of matches) if (currentPath === m || currentPath.startsWith(m)) return true;
  }
  return false;
}

function label(item, t) {
  return item.tKey ? t(item.tKey) : item.label;
}

/* ---------- bottom tab bar ---------- */

export function MobileBottomNav({ role = "student", onOpenMenu }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { main } = navFor(role);

  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-40 ux-surface border-t ux-border flex items-stretch"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      {main.map((item) => {
        const Icon = item.icon;
        const active = isActivePath(item.to, pathname, item.matches);
        return (
          <Link
            key={item.id}
            to={item.to}
            className={cn(
              "flex-1 flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px] transition-colors",
              active ? "text-indigo-600 dark:text-indigo-400" : "ux-text-muted"
            )}
          >
            <Icon className="w-[22px] h-[22px]" strokeWidth={active ? 2.4 : 2} />
            <span className="text-[10px] font-medium">{item.shortKey ? t(item.shortKey) : item.label}</span>
          </Link>
        );
      })}
      <button
        type="button"
        onClick={onOpenMenu}
        className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px] ux-text-muted"
      >
        <Menu className="w-[22px] h-[22px]" strokeWidth={2} />
        <span className="text-[10px] font-medium">{t("sidebar.more")}</span>
      </button>
    </nav>
  );
}

/* ---------- slide-in drawer (full nav) ---------- */

export function MobileDrawer({ open, onClose, role = "student", userName = "Student", onSignOut }) {
  const brand = useBrand();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { main, more } = navFor(role);

  // Coding Profile is ship-dark — hide its row until the backend probe
  // confirms the flag is on (probe memoized in codingProfile.service).
  // Only probe when this role's nav actually contains the item.
  const hasCodingItem = more.some((i) => i.id === "coding-profile");
  const [codingEnabled, setCodingEnabled] = useState(false);
  useEffect(() => {
    if (!hasCodingItem) return undefined;
    let on = true;
    isCodingProfilesEnabled().then((v) => { if (on) setCodingEnabled(v); });
    return () => { on = false; };
  }, [hasCodingItem]);
  const moreItems = codingEnabled ? more : more.filter((i) => i.id !== "coding-profile");

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const Row = ({ item }) => {
    const Icon = item.icon;
    const active = isActivePath(item.to, pathname, item.matches);
    return (
      <Link
        to={item.to}
        onClick={onClose}
        className={cn(
          "flex items-center gap-3 h-12 px-3 rounded-xl transition-colors",
          active ? "bg-indigo-50 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-300" : "ux-text hover:ux-surface-2"
        )}
      >
        <Icon className="w-5 h-5 flex-none" strokeWidth={2} />
        <span className="text-[14px] font-medium">{label(item, t)}</span>
      </Link>
    );
  };

  return (
    <div
      className={cn("md:hidden fixed inset-0 z-[60] transition-[visibility]", open ? "visible" : "invisible")}
      aria-hidden={!open}
    >
      <div
        onClick={onClose}
        className={cn("absolute inset-0 bg-black/40 transition-opacity duration-300", open ? "opacity-100" : "opacity-0")}
      />
      <aside
        className={cn(
          "absolute top-0 left-0 h-full w-[82%] max-w-[320px] ux-surface border-r ux-border flex flex-col",
          "transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]",
          open ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex items-center justify-between px-4 h-16 border-b ux-border">
          <div className="flex items-center gap-2.5">
            <BrandIcon className="w-9 h-9 flex-none" />
            <div className="min-w-0">
              <div className="text-[15px] font-semibold ux-text tracking-tight leading-tight truncate">{brand.title}</div>
              <div className="text-[10px] uppercase tracking-[0.14em] ux-text-muted">{brand.institution ? brand.product : "Exam Portal"}</div>
            </div>
          </div>
          <button onClick={onClose} className="ux-ring w-9 h-9 rounded-xl flex items-center justify-center ux-text-muted hover:ux-text hover:ux-surface-2" aria-label={t("sidebar.close_menu")}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-3 flex flex-col gap-1">
          <div className="text-[10px] font-semibold uppercase tracking-[0.12em] ux-text-muted px-3 mt-1 mb-1">{t("sidebar.section_main")}</div>
          {main.map((item) => <Row key={item.id} item={item} />)}
          <div className="text-[10px] font-semibold uppercase tracking-[0.12em] ux-text-muted px-3 mt-3 mb-1">{t("sidebar.section_account")}</div>
          {moreItems.map((item) => <Row key={item.id} item={item} />)}
        </div>

        <div className="border-t ux-border p-3 flex flex-col gap-2">
          {onSignOut && (
            <button
              onClick={() => { onClose(); onSignOut(); }}
              className="flex items-center gap-3 h-11 px-3 rounded-xl ux-text-muted hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span className="text-[14px] font-medium">{t("common.logout")}</span>
            </button>
          )}
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-9 h-9 rounded-xl ux-accent-bg text-white text-[13px] font-semibold flex items-center justify-center flex-none">
              {userName.slice(0, 1).toUpperCase()}
            </div>
            <div>
              <div className="text-[13px] font-semibold ux-text leading-tight">{userName}</div>
              <div className="text-[11px] ux-text-muted">{t(role === "teacher" ? "sidebar.role_teacher" : "sidebar.role_student")}</div>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}
